import React, {useState} from "react"
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material"
import BlockIcon from "@mui/icons-material/Block"

import {dismissalReasonIDs, phraseDismissalReason} from "./chips"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

// Assigning is the everyday path and each candidate carries its own button, so
// the dismissal sits behind a deliberate step rather than beside them: an agent
// closing a message out of the queue should not be one misplaced click from it.
function DismissPanel({
	channel,
	conversationID,
	providerMessageID,
	agentEmail,
	onDismissed,
}: {
	channel: string
	conversationID: string
	providerMessageID: string
	agentEmail: string | undefined
	onDismissed: () => void
}) {
	const {notification} = useNotification()
	const [open, setOpen] = useState(false)
	const [reason, setReason] = useState("")
	const [note, setNote] = useState("")
	const [dismissing, setDismissing] = useState(false)

	// Reopening on a fresh message must not inherit the last one's answers.
	const openDialog = () => {
		setReason("")
		setNote("")
		setOpen(true)
	}

	const handleDismiss = async () => {
		setDismissing(true)

		try {
			await api.inbox.dismissUnidentified(channel, conversationID, providerMessageID, {
				Reason: reason,
				Note: note,
				AgentEmail: agentEmail,
			})

			notification.success("Message dismissed")
			// The panel closes its own dialog rather than leaving it to whatever
			// onDismissed happens to do; today that reread also unmounts this.
			setOpen(false)
			onDismissed()
		} catch (error: any) {
			notification.error(error.Message || "Failed to dismiss the message")
		} finally {
			setDismissing(false)
		}
	}

	return (
		<>
			<Divider />
			<Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
				<Typography variant="body2" color="textSecondary">
					No customer to serve?
				</Typography>
				<Button size="small" color="error" startIcon={<BlockIcon />} onClick={openDialog}>
					Dismiss message
				</Button>
			</Box>

			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Dismiss this message</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary" mb={2}>
						It leaves the queue. The thread is untouched and it can still be assigned later.
					</Typography>
					<Box display="flex" flexDirection="column" gap={2} pt={1}>
						<FormControl fullWidth size="small">
							<InputLabel id="dismiss-reason-label">Reason</InputLabel>
							<Select
								labelId="dismiss-reason-label"
								label="Reason"
								value={reason}
								onChange={(event) => setReason(event.target.value)}
							>
								{dismissalReasonIDs.map((id) => (
									<MenuItem key={id} value={id}>
										{phraseDismissalReason(id)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Note (optional)"
							size="small"
							fullWidth
							multiline
							minRows={2}
							value={note}
							onChange={(event) => setNote(event.target.value)}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button variant="text" onClick={() => setOpen(false)} disabled={dismissing}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						disableElevation
						disabled={!reason || dismissing}
						onClick={handleDismiss}
					>
						Dismiss
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export {DismissPanel}
